import { useCalendarTime } from "components/Dashboard/DoctorDashboard/views/one-one-sessions/contexts/CalendarTimeContext";
import moment from "moment-timezone";
import { useCallback, useMemo } from "react";
import { Button } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import {
  SidesheetType,
  closeSidesheet,
  selectCurrentSidesheet,
  selectFocusedDay,
  showDayPreview,
} from "store/calendarSidebarSlice";
import { AmplitudePageName } from "utils/amplitude";
import getClasses from "utils/getClasses";
import { useCalendarSidebarContext } from "../../../../../calendar-sidebar/context/CalendarSidebarContext";
import AppointmentTimeslot from "../../../scrollable-calendar-view/components/appointment-timeslot/AppointmentTimeslot";
import useCalendarAppointmentDrop from "../../../scrollable-calendar-view/components/time-entry/hooks/useCalendarAppointmentDrop";
import { MonthlyViewEventSlot } from "../../monthly-view.types";
import SessionEntry from "../session-entry/SessionEntry";
import styles from "./EntryCell.module.scss";

/** measured manually */
const HEIGHT_OF_NON_SESSION_SPACE = 46;

/** session button height and padding in px */
const SESSION_HEIGHT = 26;

type Props = {
  isSameMonth: boolean;
  isToday: boolean;
  height: number;
  slots: MonthlyViewEventSlot[];
  startValue: number;
  hasScheduleIntervals: boolean;
  amplitudePageName: AmplitudePageName;
};
const EntryCell = ({
  isSameMonth,
  isToday,
  height,
  slots,
  startValue,
  hasScheduleIntervals,
  amplitudePageName,
}: Props) => {
  const { setCurrentTimeRangeType, setAnchorTime, currentCalendarTimezone } =
    useCalendarTime();

  const dispatch = useDispatch();
  const currentSidesheet = useSelector(selectCurrentSidesheet);
  const focusedDay = useSelector(selectFocusedDay);

  const { requestCancelAddNewSession } = useCalendarSidebarContext();

  const isFocused = useMemo(
    () => focusedDay === startValue,
    [focusedDay, startValue],
  );

  const isUnavailable = useMemo(
    () => !hasScheduleIntervals,
    [hasScheduleIntervals],
  );

  const getRescheduledAppointment = useCallback(
    (appointment: AppointmentWithSeriesInfo & { invoice?: InvoiceData }) => {
      const oldAppointmentStart = moment.tz(
        appointment.appointment_start,
        currentCalendarTimezone,
      );
      const durationMinutes = moment
        .tz(appointment.appointment_end, currentCalendarTimezone)
        .diff(oldAppointmentStart, "minutes");

      const diffDays = moment
        .tz(startValue, currentCalendarTimezone)
        .startOf("day")
        .diff(
          moment
            .tz(oldAppointmentStart, currentCalendarTimezone)
            .startOf("day"),
          "days",
        );

      const newAppointmentStart = moment
        .tz(oldAppointmentStart, currentCalendarTimezone)
        .add(diffDays, "days");
      const newAppointmentEnd = moment
        .tz(newAppointmentStart, currentCalendarTimezone)
        .add(durationMinutes, "minutes");

      return {
        ...appointment,
        appointment_start: newAppointmentStart.toISOString(),
        appointment_end: newAppointmentEnd.toISOString(),
      };
    },
    [startValue, currentCalendarTimezone],
  );

  const { drop, temporaryAppointment } = useCalendarAppointmentDrop({
    getRescheduledAppointment,
  });

  const slotsAdjustedForTemporaryAppoinement = useMemo(() => {
    if (!temporaryAppointment) {
      return slots;
    }

    let foundPlaceholder = false;
    return slots.filter((slot) => {
      if (foundPlaceholder) {
        return true;
      }

      if (slot.type === "EVENT_CONTINUED") {
        foundPlaceholder = true;
        return false;
      }

      return true;
    });
  }, [slots, temporaryAppointment]);

  const maxSessions = useMemo(
    () =>
      Math.max(
        Math.floor((height - HEIGHT_OF_NON_SESSION_SPACE) / SESSION_HEIGHT),
        0,
      ),
    [height],
  );

  const hiddenSessionCount = useMemo(
    () =>
      slotsAdjustedForTemporaryAppoinement
        .slice(maxSessions)
        .filter((el) => el.type !== "EMPTY").length,
    [maxSessions, slotsAdjustedForTemporaryAppoinement],
  );

  const shouldShowEllipsis = useMemo(
    () => hiddenSessionCount > 0,
    [hiddenSessionCount],
  );

  const dayLabel = useMemo(
    () =>
      moment.tz(startValue, currentCalendarTimezone).startOf("day").format("D"),
    [startValue, currentCalendarTimezone],
  );

  const onClick = useCallback(() => {
    if (
      currentSidesheet === SidesheetType.AddToCalendar &&
      requestCancelAddNewSession
    ) {
      requestCancelAddNewSession();
      return;
    }

    switch (currentSidesheet) {
      case SidesheetType.None:
      case SidesheetType.DayPreview:
        dispatch(showDayPreview(startValue));
        break;
      default:
        dispatch(closeSidesheet());
    }
  }, [currentSidesheet, dispatch, startValue, requestCancelAddNewSession]);

  return (
    <td ref={drop} className={getClasses(styles["entry-cell"])}>
      <div
        className={getClasses(
          styles["entry-cell-timeslot"],
          isUnavailable && styles["entry-cell-timeslot--unavailable"],
          isFocused && styles["entry-cell-timeslot--focused"],
        )}
        onClick={onClick}
      />
      {/* must use a wrapper to control the height */}
      <div className={styles["content"]} style={{ height: height }}>
        <div
          className={getClasses(
            styles["day-label"],
            isUnavailable && styles["day-label--unavailable"],
            isToday && styles["day-label--today"],
            !isSameMonth && styles["day-label--diff-month"],
          )}
        >
          {dayLabel}
        </div>
        {temporaryAppointment ? (
          <AppointmentTimeslot
            appointment={temporaryAppointment}
            isDndPreview
            isInvalidDndPreview={
              new Date(temporaryAppointment.appointment_start).getTime() <
              Date.now()
            }
            amplitudePageName={amplitudePageName}
          />
        ) : null}
        {slotsAdjustedForTemporaryAppoinement
          .slice(0, maxSessions)
          .map((slot) => (
            <SessionEntry
              key={slot.key}
              slot={slot}
              amplitudePageName={amplitudePageName}
            />
          ))}
        {/* information and go to link  */}
        {shouldShowEllipsis && (
          <Button
            variant="link"
            size="sm"
            onClick={() => {
              setAnchorTime(startValue);
              setCurrentTimeRangeType("week");
            }}
            className={styles["view-more"]}
          >
            + {hiddenSessionCount}
          </Button>
        )}
      </div>
    </td>
  );
};

export default EntryCell;
